import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import PurchaseAppService from '../../../../../application/platform/scp/purchase/purchase-app-service';
import { useFeedback } from '../../../../../hooks/use-feedback';
import { hasErrors } from '../../../../../utils/validation';
import { validatePurchaseItemResult } from '../../context/purchase-validation';
import { IUpdatePurchaseItemResultRequest } from '../../../../../model/scp/purchase-item-result/request/update-purchase-item-result-request';
import { IPurchaseItemResultResponse } from '../../../../../model/scp/purchase-item-result/response/purchase-item-result-response';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';

// O mesmo formulário atende inclusão e alteração; situação e justificativa só são usadas na alteração
export type PurchaseItemResultForm = IUpdatePurchaseItemResultRequest;

const DEFAULT_CHANGE_JUSTIFICATION = 'Alteracao de registro de preco';

function emptyResult(): PurchaseItemResultForm {
    return {
        approvedQuantity: 0,
        approvedUnitPrice: 0,
        approvedTotalPrice: 0,
        discountPercentage: 0,
        personTypeId: '',
        supplierNi: '',
        supplierCompanyName: '',
        supplierSizeId: 0,
        countryCode: '',
        subcontractingIndicator: false,
        meEppBenefitApplication: false,
        srpClassificationOrder: 0,
        resultDate: new Date(),
        legalNatureId: '',
        descriptionBrand: '',
        itemResultStatusId: 0,
    };
}

function toResultForm(data: IPurchaseItemResultResponse): PurchaseItemResultForm {
    return {
        approvedQuantity: data.approvedQuantity || 0,
        approvedUnitPrice: data.approvedUnitPrice || 0,
        approvedTotalPrice: data.approvedTotalPrice || 0,
        discountPercentage: data.discountPercentage || 0,
        itemResultStatusId: data.itemResultStatusId || 1,
        personTypeId: data.personTypeId || '',
        supplierNi: data.supplierNi || '',
        supplierCompanyName: data.supplierCompanyName || '',
        supplierSizeId: data.supplierSizeId || 0,
        countryCode: data.countryCode || '',
        subcontractingIndicator: data.subcontractingIndicator || false,
        meEppBenefitApplication: data.meEppBenefitApplication || false,
        srpClassificationOrder: data.srpClassificationOrder || 0,
        resultDate: data.resultDate || new Date(),
        legalNatureId: data.legalNatureId || '',
        situationId: data.situationId,
        descriptionBrand: (data as any).descriptionBrand || '',
        justification: DEFAULT_CHANGE_JUSTIFICATION,
    };
}

export const usePurchaseItemResultController = ({ purchaseId, itemNumber, resultData, onSaved, onClose }: {
    purchaseId: number;
    itemNumber: number;
    resultData?: IPurchaseItemResultResponse | null;
    onSaved: () => void;
    onClose: () => void;
}) => {
    const { showError, showSuccess, withLoading } = useFeedback();
    const isEditMode = !!resultData;
    const [resultRequest, setResultRequest] = useState<PurchaseItemResultForm>(() => (resultData ? toResultForm(resultData) : emptyResult()));
    const [legalNatures, setLegalNatures] = useState<ITableResponse[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    useEffect(() => {
        BaseAppService.listLegalNature()
            .then(setLegalNatures)
            .catch((error: any) => showError('Não foi possível carregar as naturezas jurídicas', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function update(patch: Partial<PurchaseItemResultForm>) {
        setResultRequest((previous) => {
            const next = { ...previous, ...patch };
            return { ...next, approvedTotalPrice: (next.approvedQuantity || 0) * (next.approvedUnitPrice || 0) };
        });
    }

    function handleOnChange(e: any) {
        const { name, value, type, checked } = e.target;
        update({ [name]: type === 'checkbox' ? checked : value });
    }

    function handleResultDateChange(value: string | null) {
        update({ resultDate: value ? dayjs(value).toDate() : new Date() });
    }

    async function handleSave() {
        if (isSaving) return;
        if (hasErrors(validatePurchaseItemResult(resultRequest))) {
            setShowValidationErrors(true);
            return;
        }
        setIsSaving(true);
        try {
            await withLoading(async () => {
                if (resultData) {
                    await PurchaseAppService.updatePurchaseItemResult(purchaseId, itemNumber, resultData.resultSequence, resultRequest);
                } else {
                    await PurchaseAppService.addPurchaseItemResult(purchaseId, itemNumber, resultRequest);
                }
            });
            showSuccess(isEditMode ? 'Resultado alterado com sucesso!' : 'Resultado adicionado com sucesso!');
            onSaved();
            onClose();
        } catch (error: any) {
            showError('Não foi possível salvar o resultado', error);
        } finally {
            setIsSaving(false);
        }
    }

    return {
        resultRequest,
        isEditMode,
        legalNatures,
        isSaving,
        validationErrors: showValidationErrors ? validatePurchaseItemResult(resultRequest) : {},
        update,
        handleOnChange,
        handleResultDateChange,
        handleSave,
    };
};
